import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { useBaseLogin } from '@/api/react-query'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormFieldGroup, FormContainer, useAppForm } from '@/components/form/form'
import { authToken } from '@/hooks/use-auth'
import { useI18n } from '@/i18n/i18n-react'
import { api } from '@/main'

export const Route = createFileRoute('/login')({
	component: LoginForm,
	validateSearch: z.object({ redirect: z.string().optional() }),
	staticData: {
		crumb: ({ context: { L } }) => L.Login.PageTitle()
	}
})

function LoginForm() {
	const navigate = useNavigate()
	const { redirect } = Route.useSearch()
	const { L } = useI18n()
	const login = useBaseLogin()
	const form = useAppForm({
		defaultValues: { email: '', password: '' },
		onSubmit: async ({ value }) => {
			const result = await login({
				body: {
					email: value.email,
					password: value.password
				}
			})
			if (result.isSuccess) {
				authToken.next(result.response.token)
				api.resetSessionExpiredFlag()
				navigate({ to: redirect || '/analytics' })
			} else {
				toast.error(result.status === 403 ? L.Login.UnauthorizedError() : L.Login.Error())
			}
		}
	})

	return (
		<div className='flex min-h-dvh w-dvw items-center justify-center bg-ui-bg-subtle'>
			<div className='m-4 flex w-full max-w-[280px] flex-col items-center'>
				{/* <AvatarBox /> */}
				<div className='mb-4 flex flex-col items-center'>
					<Heading>{L.Login.Title()}</Heading>
					<Text size='small' className='text-center text-ui-fg-subtle'>
						{L.Login.Description()}
					</Text>
				</div>
				<form.AppForm>
					<FormContainer>
						<FormFieldGroup>
							<form.AppField
								name='email'
								children={field => <field.TextField placeholder={L.Login.Email()} />}
							/>
							<form.AppField
								name='password'
								children={field => <field.TextField placeholder={L.Login.Password()} type='password' />}
							/>
						</FormFieldGroup>
						<form.SaveButton label={L.Login.LogIn()} />
					</FormContainer>
				</form.AppForm>
				<span className='txt-small my-6 text-ui-fg-muted'>
					<Link
						key='reset-password-link'
						to='/reset-password'
						className='font-medium text-ui-fg-interactive outline-none transition-fg hover:text-ui-fg-interactive-hover focus-visible:text-ui-fg-interactive-hover'
					>
						{L.Login.ForgotPassword()}
					</Link>
				</span>
			</div>
		</div>
	)
}
